import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Loader2, ShieldCheck, User as UserIcon } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import * as apiLib from "@/lib/api";

const EMPTY = { email: "", name: "", password: "", role: "user" };

export const UsersDialog = ({ open, onOpenChange, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setUsers(await apiLib.fetchUsers()); }
    catch (e) { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) { load(); setForm(EMPTY); } }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.email.trim() || !form.name.trim() || form.password.length < 6) {
      toast.error("Name, email and a 6+ char password are required");
      return;
    }
    setCreating(true);
    try {
      await apiLib.createUser({ ...form, email: form.email.trim().toLowerCase() });
      toast.success("User created");
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(apiLib.formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (u) => {
    try {
      await apiLib.deleteUser(u.id);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(apiLib.formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="users-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold tracking-tight">Manage Users</DialogTitle>
          <DialogDescription>Create accounts for people who should access this launcher.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 rounded-xl border border-border p-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="u-name">Name</Label>
              <Input id="u-name" value={form.name} onChange={(e) => set("name", e.target.value)}
                data-testid="user-name" placeholder="Jane Doe" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="u-email">Email</Label>
              <Input id="u-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                data-testid="user-email" placeholder="jane@example.com" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="u-pass">Password</Label>
              <Input id="u-pass" type="text" value={form.password} onChange={(e) => set("password", e.target.value)}
                data-testid="user-password" placeholder="min 6 characters" />
            </div>
            <div className="grid gap-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={submit} disabled={creating} className="w-full" data-testid="create-user-submit">
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
            Create User
          </Button>
        </div>

        <div className="mt-2">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Existing users
          </p>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="flex flex-col gap-2" data-testid="users-list">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`user-row-${u.id}`}>
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg text-white ${u.role === "admin" ? "bg-primary" : "bg-zinc-500"}`}>
                    {u.role === "admin" ? <ShieldCheck className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{u.name} {u.id === currentUser?.id && <span className="text-xs text-muted-foreground">(you)</span>}</p>
                    <p className="truncate text-xs text-muted-foreground">{u.email} · {u.role}</p>
                  </div>
                  {u.id !== currentUser?.id && (
                    <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}
                      className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
